import { z } from 'zod';
export const OperationName = z.enum([
    'claim', 'release', 'send', 'create-ticket', 'link-ticket', 'resolve-identity',
    'request-otp', 'verify-otp', 'hand-off', 'force-sync', 'close-thread',
    'close-ticket-cascade', 'log-call', 'set-ai-hints', 'add-note', 'start-outbound',
]);
const threadId = z.string().min(1);
export const ClaimArgs = z.object({ threadId });
export const ReleaseArgs = z.object({ threadId });
export const SendArgs = z.object({
    threadId,
    body: z.string().min(1),
    mediaIds: z.array(z.string().min(1)).max(10).optional(),
});
export const CreateTicketArgs = z.object({
    threadId,
    subject: z.string().min(1),
    /** 1A: create as the configured Quick Fix type instead of the default. The
     *  type ID itself lives in config (`h.quickfix`) — callers say WHAT they
     *  want, never which Halo id that is. */
    quickFix: z.boolean().optional(),
});
export const LinkTicketArgs = z.object({ threadId, ticket: z.string().min(1) });
export const ResolveIdentityArgs = z.object({
    threadId,
    action: z.enum(['match', 'create-contact', 'assign-client', 'dismiss', 'park']),
    contactId: z.string().optional(),
    clientId: z.string().optional(),
    /** true = write the mapping back to the Halo contact; false = alias table only */
    writeBackToHalo: z.boolean().default(true),
    /**
     * Remember this handle as this contact for FUTURE conversations.
     *
     * Defaults false, and that default is the point. Matching answers "who is
     * this call from". An alias answers "who is this number, always" — and for a
     * shared main line those have different answers, because eighty-four people
     * ring from it. Binding the number to whoever happened to call first would
     * mislabel every later call from that company, silently.
     */
    aliasHandle: z.boolean().default(false),
    name: z.string().optional(),
});
export const RequestOtpArgs = z.object({
    threadId,
    method: z.enum(['sms', 'email']),
    /** Must match a destination of record on the Halo contact. Never a supplied handle. */
    destination: z.string().min(1),
});
export const VerifyOtpArgs = z.object({ threadId, code: z.string().length(6) });
export const HandOffArgs = z.object({
    threadId,
    techId: z.string().min(1),
    internalState: z.enum(['live-warm', 'scheduled']),
    clientDisclosure: z.enum(['none', 'now', 'sla-range', 'exact-time']),
    at: z.string().optional(),
    /** The operator's own words for the client-facing hand-off cue. Never a hardcoded SLA phrase. */
    clientMessage: z.string().min(1).optional(),
});
export const ForceSyncArgs = z.object({ threadId });
export const CloseThreadArgs = z.object({
    threadId,
    note: z.string().optional(),
    clientMessage: z.string().optional(),
    /** 1A: also close the linked Halo ticket once the close note lands. Opt-in
     *  and explicit — the console sends it only from the Quick Fix flow. */
    closeTicket: z.boolean().optional(),
});
export const CloseTicketCascadeArgs = z.object({ ticket: z.string().min(1) });
/**
 * One fragment of a technician's running notes on a live call.
 *
 * Deliberately NOT `log-call`. That operation is a disposition: it carries
 * `billable` and writes a Halo time entry, so reusing it would turn every
 * half-sentence typed mid-call into a billable time record. This is the
 * cheap, repeatable one — a dispatcher hits enter on "printer does nothing"
 * and keeps listening.
 */
export const AddNoteArgs = z.object({ threadId, body: z.string().min(1) });
export const LogCallArgs = z.object({
    threadId,
    note: z.string().min(1),
    billable: z.boolean(),
    attachRecording: z.boolean().default(false),
});
/**
 * Switch the AI tab on or off for ONE conversation.
 *
 * Not a preference: the answer lives on the thread, so a tech who decides the
 * hints are not worth paying for here stops the spend for everyone who opens
 * it. A new thread starts on again, which makes closing a conversation the
 * reset.
 */
export const SetAiHintsArgs = z.object({
    threadId,
    enabled: z.boolean(),
});
/**
 * Thread-free by design: this operation CREATES the thread (or adopts the one
 * open thread already on this handle). The handle is a claim until the route
 * verifies it against the picked contact's own destinations of record — a
 * Matched identity must never be stamped onto an arbitrary number.
 */
export const StartOutboundPersonArgs = z.object({
    channel: z.enum(['sms', 'chat']),
    haloContactId: z.string().min(1),
    handle: z.string().min(1),
    ticketId: z.string().min(1).optional(),
    body: z.string().min(1),
});
/** Device threads are chat by definition — a machine has no phone number. */
export const StartOutboundDeviceArgs = z.object({
    channel: z.literal('chat'),
    deviceId: z.string().min(1),
    ticketId: z.string().min(1).optional(),
    body: z.string().min(1),
});
// Union order matters: the device schema is tried first, so a body carrying
// `deviceId` always parses as device — including one that ALSO carries
// haloContactId (a caller cannot get a person-shaped parse by tacking on
// device fields; there is no ambiguity to resolve downstream). Person bodies
// lack `deviceId` and fall through to the person schema.
export const StartOutboundArgs = z.union([StartOutboundDeviceArgs, StartOutboundPersonArgs]);
export const OPERATION_ARGS = {
    'claim': ClaimArgs,
    'release': ReleaseArgs,
    'send': SendArgs,
    'create-ticket': CreateTicketArgs,
    'link-ticket': LinkTicketArgs,
    'resolve-identity': ResolveIdentityArgs,
    'request-otp': RequestOtpArgs,
    'verify-otp': VerifyOtpArgs,
    'hand-off': HandOffArgs,
    'force-sync': ForceSyncArgs,
    'close-thread': CloseThreadArgs,
    'close-ticket-cascade': CloseTicketCascadeArgs,
    'log-call': LogCallArgs,
    'set-ai-hints': SetAiHintsArgs,
    'add-note': AddNoteArgs,
    'start-outbound': StartOutboundArgs,
};
